import { Sheet, Box, Typography, IconButton, Drawer, useColorScheme } from '@mui/joy';
import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import HomeIcon from '@mui/icons-material/Home';
import InfoIcon from '@mui/icons-material/Info';
import WorkIcon from '@mui/icons-material/Work';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import logo from '../../assets/logo.png';


const navItems = [
    { path: '/', label: 'Home', icon: HomeIcon },
    { path: '/about', label: 'About', icon: InfoIcon },
    { path: '/careers', label: 'Careers', icon: WorkIcon },
];

const NavLink = ({ to, icon: Icon, children, isActive }) => (
    <Link 
        to={to} 
        style={{ textDecoration: 'none', color: 'inherit' }}
    >
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: 'md',
                position: 'relative',
                transition: 'all 0.2s ease',
                '&:hover': {
                    bgcolor: 'background.level1',
                }
            }}
        >
        <Icon 
            sx={{ 
                fontSize: 20,
                color: isActive ? 'primary.main' : 'text.secondary',
                transition: 'color 0.2s ease'
            }} 
        />
        <Typography
            sx={{
            color: isActive ? 'primary.main' : 'text.primary',
            fontWeight: isActive ? 600 : 400,
            transition: 'all 0.2s ease'
            }}
        >
            {children}
        </Typography>
        <Box
            sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '100%',
                height: 2,
                bgcolor: 'primary.main',
                borderRadius: 'xl',
                transform: isActive ? 'scaleX(1)' : 'scaleX(0)',
                transition: 'transform 0.2s ease',
                transformOrigin: 'left'
            }}
        />
        </Box>
    </Link>
);

const Navbar = () => {
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const { mode, setMode } = useColorScheme();

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const toggleMode = () => {
        setMode(mode === 'dark' ? 'light' : 'dark');
    };

    const MobileMenu = () => (
        <Drawer
            open={mobileOpen}
            onClose={handleDrawerToggle}
            anchor="right"
            size="md"
            sx={{
                '& .MuiDrawer-content': {
                    bgcolor: 'background.body',
                    p: 2,
                    width: '100%',
                    marginTop: '6rem',
                    maxWidth: 300,
                    borderRadius: 'md',
                },
            }}
        >
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography level="h4">Menu</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton onClick={toggleMode} variant="soft">
                    {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
                </IconButton>
                <IconButton onClick={handleDrawerToggle}>
                    <CloseIcon />
                </IconButton>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {navItems.map((item) => (
                    <NavLink
                        key={item.path}
                        to={item.path}
                        icon={item.icon}
                        isActive={location.pathname === item.path}
                        onClick={handleDrawerToggle}
                    >
                        {item.label}
                    </NavLink>
                ))}
            </Box>
        </Drawer>
    );

    return (
        <Sheet
            component="nav"
            sx={{
                position: 'sticky',
                top: 0,
                zIndex: 9999,
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                bgcolor: 'background.body',
                borderBottom: '1px solid',
                borderColor: 'divider',
                boxShadow: 'sm'
            }}
        >
            <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                
                    <img src={logo} alt="logo" style={{ width: 50, height: 50 }} />
                    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                        <Typography 
                            className="logo-text"
                            level="h4" 
                            component="div"
                            sx={{
                                fontWeight: 'bold',
                                // background: 'linear-gradient(45deg, #1a237e 30%, #0d47a1 90%)',
                                // WebkitBackgroundClip: 'text',
                                // WebkitTextFillColor: 'transparent',
                                // fontWeight: 'bold',
                                // transition: 'opacity 0.2s ease'
                            }}
                        >
                            Hubitable
                        </Typography>
                        <Typography
                            level="body-xs"
                            sx={{
                                position: 'absolute',
                                top: -5,
                                right: -28,
                                backgroundColor: 'warning.main',
                                color: 'warning.contrastText',
                                px: 0.8,
                                py: 0.2,
                                borderRadius: 'sm',
                                fontSize: '0.65rem',
                                fontWeight: 'bold',
                                textTransform: 'uppercase'
                            }}
                        >
                            Beta
                        </Typography>
                    </Box>
                </Box>
            </Link>
            
            {/* Desktop Menu */}
            <Box 
                sx={{ 
                    display: { xs: 'none', md: 'flex' }, 
                    gap: 1,
                    alignItems: 'center'
                }}
            >
                {navItems.map((item) => (
                <NavLink
                    key={item.path}
                    to={item.path}
                    icon={item.icon}
                    isActive={location.pathname === item.path}
                >
                    {item.label}
                </NavLink>
                ))}
                {/* Color Theme Mode Toggle Button */}
                <IconButton 
                    onClick={toggleMode} 
                    variant="soft"
                    sx={{ ml: 1 }}
                >
                    {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
                </IconButton>
            </Box>

            {/* Mobile Menu Button */}
            <Box
                sx={{ 
                    display: { xs: 'flex', md: 'none' },
                    gap: 1,
                    alignItems: 'center'
                }}
            >
                {/* Color Theme Mode Toggle Button */}
                <IconButton 
                    onClick={toggleMode} 
                    variant="soft"
                    sx={{ color: 'primary.main' }}
                >
                {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
                </IconButton>
                {/* Mobile Menu Button */}
                <IconButton
                onClick={handleDrawerToggle}
                sx={{ color: 'primary.main' }}
                >
                <MenuIcon />
                </IconButton>
            </Box>

            {/* Mobile Menu Drawer */}
            <MobileMenu />
        </Sheet>
    );
};

export default Navbar; 